/**
 * What the site lookup returns (ALU-157). Types and constants only, no zod:
 * the wizard's client components import this file.
 *
 * Every suggestion carries how sure the lookup is and where it came from, so
 * the admin knows what to double-check. The engine never returns people or
 * contact details: only what the institution says about itself.
 */
import type { Locale } from '@/lib/i18n/config'
import type { InstitutionKind } from '@/lib/org/institution-noun'
import type { AlumniKind } from '@/lib/org/alumni-noun'

export const CONFIDENCES = ['high', 'medium', 'low'] as const
export type Confidence = (typeof CONFIDENCES)[number]

export type Suggestion<T> = {
  value: T
  confidence: Confidence
  /** Where it came from, in plain words: "og:site_name", "theme-color", "IA", "/egresados". */
  source: string
}

export const LOGO_KINDS = ['svg', 'apple-touch-icon', 'header-logo', 'og-image', 'icon'] as const
export type LogoKind = (typeof LOGO_KINDS)[number]

export type LogoOption = {
  /** The image's URL on the institution's site. Only fetched by our server. */
  url: string
  kind: LogoKind
  source: string
  /** A 256 px PNG preview as a data URL, made on our server (never a hotlink). */
  preview: string
  width: number
  height: number
  /** Its most common opaque colour, when it has one. */
  dominantColor: string | null
}

/**
 * The institution's own structure the wizard can create as first groups:
 * faculties and degrees, houses, and city chapters. They are real groups (the
 * phase 2 demo cleanup keeps them), so the kind is kept next to the name.
 */
export const SUGGESTED_GROUP_KINDS = ['faculty', 'degree', 'house', 'chapter'] as const
export type SuggestedGroupKind = (typeof SUGGESTED_GROUP_KINDS)[number]

export type SuggestedGroup = { name: string; kind: SuggestedGroupKind }

/** The `chapter_type` each kind is created as. */
export const GROUP_KIND_CHAPTER_TYPE: Record<
  SuggestedGroupKind,
  'faculty' | 'interest' | 'geographic'
> = {
  faculty: 'faculty',
  degree: 'faculty',
  house: 'interest',
  chapter: 'geographic',
}

export type InstitutionSuggestions = {
  name: Suggestion<string> | null
  shortName: Suggestion<string> | null
  slug: Suggestion<string> | null
  /** Up to three logos, best first. */
  logos: LogoOption[]
  color: Suggestion<string> | null
  /**
   * The colour checked against the Encuentro grounds (lib/design/accent.ts):
   * `readable` is the variant text can use; `adjusted` when it had to move.
   */
  colorCheck: { readable: string; adjusted: boolean; ratio: number } | null
  language: Suggestion<Locale> | null
  /** ISO 3166-1 alpha-2. */
  country: Suggestion<string> | null
  city: Suggestion<string> | null
  /** IANA zone. */
  timezone: Suggestion<string> | null
  institutionType: Suggestion<InstitutionKind> | null
  alumniWord: Suggestion<AlumniKind> | null
  /** What they call a class year: "Promoción", "Generación", "Class of". */
  classYearTerm: Suggestion<string> | null
  /** The year it was founded; phase 2 takes the demo's class years from it. */
  foundingYear: Suggestion<number> | null
  groups: Suggestion<SuggestedGroup[]> | null
  /** The alumni platform the site already links to (Graduway, Hivebrite…), a sales signal. */
  alumniPlatform: Suggestion<string> | null
}

/** Why a lookup found less than it could. Shown as one line; never blocks the wizard. */
export const LOOKUP_NOTES = [
  'timeout',
  'blocked',
  'unreachable',
  'bot_blocked',
  'robots',
  'not_html',
  'ai_unavailable',
] as const
export type LookupNote = (typeof LOOKUP_NOTES)[number]

export type LookupResult = {
  /** The URL as normalized before fetching (`https://colegio.edu.ar/`). */
  url: string
  /** The site's host without `www.` (`colegio.edu.ar`): what `organizations.website_domain` stores. */
  domain: string
  suggestions: InstitutionSuggestions
  /** Paths read, in order (`/`, `/egresados`). */
  pages: string[]
  notes: LookupNote[]
  ai: { used: boolean; model: string | null; costUsd: number | null }
  durationMs: number
  fetchedAt: string
}

export function emptySuggestions(): InstitutionSuggestions {
  return {
    name: null,
    shortName: null,
    slug: null,
    logos: [],
    color: null,
    colorCheck: null,
    language: null,
    country: null,
    city: null,
    timezone: null,
    institutionType: null,
    alumniWord: null,
    classYearTerm: null,
    foundingYear: null,
    groups: null,
    alumniPlatform: null,
  }
}
